import { BlazeOnboarding, WorkspaceClient } from 'api/clients/WorkspaceClient'
import { makeAutoObservable } from 'mobx'
import { type RootStore } from 'stores/RootStore'
import { EventType } from 'types'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'

export enum CTA {
  Brainstorm = 'Brainstorm',
  Campaign = 'Campaign',
  BrandVoice = 'BrandVoice',
  ThirdPartyIntegrations = 'ThirdPartyIntegrations',
  BlogPost = 'BlogPost',
  BlackFriday = 'BlackFriday',
}

export const CTA_TO_SKIP_EVENT: Record<CTA, EventType> = {
  [CTA.Brainstorm]: 'Event::BrainstormSkipped',
  [CTA.Campaign]: 'Event::ContentCampaignSkipped',
  [CTA.BrandVoice]: 'Event::BrandVoiceSkipped',
  [CTA.ThirdPartyIntegrations]: 'Event::ThirdPartyIntegrationsSkipped',
  [CTA.BlogPost]: 'Event::BlogPostSkipped',
  [CTA.BlackFriday]: 'Event::BlackFridaySkipped',
}

// Which of the CTAs should be shown to the user
// TODO: Enable ThirdPartyIntegrations once Zapier has approved us GO-2277
const ACTIVE_CTAS: CTA[] = [CTA.BlogPost, CTA.Campaign, CTA.BrandVoice]

// keeps tracks of what order the CTAs should be shown in
const orderedCTAs: CTA[] = [CTA.BrandVoice, CTA.BlogPost, CTA.Brainstorm, CTA.Campaign, CTA.ThirdPartyIntegrations]

export class OnboardingStore {
  rootStore: RootStore

  private _completedActions: Set<CTA> = new Set()

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  get currentCTA() {
    return orderedCTAs.find((cta) => ACTIVE_CTAS.includes(cta) && !this._completedActions.has(cta))
  }

  get completedActions() {
    return this._completedActions
  }

  setCompletedActionsFromAPI(data: BlazeOnboarding) {
    if (data.brand_voice_created) {
      this._completedActions.add(CTA.BrandVoice)
    }
    if (data.blog_post_created) {
      this._completedActions.add(CTA.BlogPost)
    }
    if (data.content_campaign_created) {
      this._completedActions.add(CTA.Campaign)
    }
    if (data.third_party_integration_created) {
      this._completedActions.add(CTA.ThirdPartyIntegrations)
    }
    if (data.brainstorm_created) {
      this._completedActions.add(CTA.Brainstorm)
    }
  }

  markCTAAsCompleted(cta: CTA) {
    this._completedActions.add(cta)
  }

  ctaCompleted(cta: CTA) {
    return this._completedActions.has(cta)
  }

  hydrate = async (workspaceKey: string) => {
    const response = await WorkspaceClient.fetchBlazeOnboarding(workspaceKey)

    if (response.type === SUCCESS_RESPONSE_TYPE) {
      this.setCompletedActionsFromAPI(response.data)
    }
  }
}
